// Shared display formatters for candidate experience + salary.
// Kept in one place so the submission tile, interview tile, and any future
// consumer render identical strings (previously duplicated in Submissions.js).

// Experience in years → "1 yrs 3 mos" / "2 yrs" / "5 mos". Returns null for
// null/0/negative so callers can hide the row. Avoids raw floats like
// "1.0833 yrs".
export const fmtExp = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n <= 0) return null;
  const y = Math.floor(n);
  const m = Math.round((n - y) * 12);
  if (y > 0 && m > 0) return `${y} yrs ${m} mos`;
  if (y > 0) return `${y} yrs`;
  if (m > 0) return `${m} mos`;
  return null;
};

// Salary formatter. Large values are abbreviated so they don't wrap tiles
// (INR → Lakh/Crore per Indian convention; other currencies → k/M). Values
// below the abbreviation threshold are shown IN FULL with thousands
// separators, so small/precise amounts are never rounded away
// (e.g. "USD 4444" → "$4,444", NOT "$4k"). Abbreviated values keep up to 2
// decimals so precision loss stays negligible ("₹8.5L", "$124.5k").
export const CURRENCY_SYMBOL = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', SGD: 'S$', AED: 'AED ',
};

export const fmtCTC = (val, ccy = 'INR') => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return null;
  const sym = CURRENCY_SYMBOL[ccy] || `${ccy} `;
  const trim = (x) => `${+x.toFixed(2)}`;               // 8.50 → "8.5", 8.00 → "8"
  if (ccy === 'INR') {
    if (n >= 1e7) return `${sym}${trim(n / 1e7)}Cr`;    // ≥ 1 crore
    if (n >= 1e5) return `${sym}${trim(n / 1e5)}L`;     // ≥ 1 lakh
    return `${sym}${n.toLocaleString('en-IN')}`;        // full: ₹4,444
  }
  if (n >= 1e6) return `${sym}${trim(n / 1e6)}M`;       // ≥ 1 million
  if (n >= 1e5) return `${sym}${trim(n / 1e3)}k`;       // ≥ 100k → 120k / 124.5k
  return `${sym}${n.toLocaleString('en-US')}`;          // full: $4,444
};

// Combine current → expected into one CTC string; returns null if neither set.
export const fmtCTCRange = (curVal, curCcy, expVal, expCcy) => {
  const c = fmtCTC(curVal, curCcy);
  const e = fmtCTC(expVal, expCcy);
  if (c && e) return `${c} → ${e}`;
  return c || e || null;
};

// ── Interview times ───────────────────────────────────────────────────────────
// Times are stored as 24-hour "HH:MM" strings on the round (time / endTime).

// "18:30" → "6:30 PM". Null for blank/malformed so callers can hide the row.
export const fmtTime12 = (t) => {
  const m = String(t || '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const hr = Number(m[1]);
  if (hr > 23 || Number(m[2]) > 59) return null;
  return `${hr % 12 || 12}:${m[2]} ${hr < 12 ? 'AM' : 'PM'}`;
};

// Minutes past midnight for "HH:MM", or null when unusable.
export const timeToMinutes = (t) => {
  const m = String(t || '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const [h, mi] = [Number(m[1]), Number(m[2])];
  return (h > 23 || mi > 59) ? null : h * 60 + mi;
};

// "18:30" + "19:00" → "6:30 PM – 7:00 PM". Falls back to the start alone when
// there is no end time or it isn't after the start — which is what every round
// scheduled before end times existed looks like. Null when there's no start.
export const fmtTimeRange = (start, end) => {
  const s = fmtTime12(start);
  if (!s) return null;
  const e = fmtTime12(end);
  const [sm, em] = [timeToMinutes(start), timeToMinutes(end)];
  return (e && sm != null && em != null && em > sm) ? `${s} – ${e}` : s;
};

// Start time + minutes → "HH:MM", clamped to the same day so a late slot can't
// silently wrap past midnight. Null when the start is unusable.
export const addMinutesToTime = (start, mins) => {
  const sm = timeToMinutes(start);
  if (sm == null) return null;
  const total = Math.min(sm + mins, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// ── LPA (Lakhs Per Annum) ─────────────────────────────────────────────────────
// Salary/budget shown in LPA. DISPLAY-ONLY heuristic (stored value never changes):
// a number >= 1000 is treated as raw rupees and converted to lakhs (÷ 100000);
// a number < 1000 is assumed to already be in lakhs. So:
//   1500000 → 15 LPA   ·   4400000 → 44 LPA   ·   15 → 15 LPA
// Returns null for empty/invalid so callers can hide the row.
export const toLPA = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n >= 1000 ? n / 1e5 : n;
};

const trimNum = (x) => `${+x.toFixed(2)}`;   // 15.00 → "15", 15.50 → "15.5"

// Single value → "15 LPA". Null when empty/invalid.
export const fmtLPA = (val) => {
  const l = toLPA(val);
  return l == null ? null : `${trimNum(l)} LPA`;
};

// Range (min–max or current→expected) → "15–20 LPA". Falls back to whichever
// side is set; null when neither is.
export const fmtLPARange = (minVal, maxVal) => {
  const lo = toLPA(minVal);
  const hi = toLPA(maxVal);
  if (lo != null && hi != null) return `${trimNum(lo)}–${trimNum(hi)} LPA`;
  if (lo != null) return `${trimNum(lo)} LPA`;
  if (hi != null) return `${trimNum(hi)} LPA`;
  return null;
};
